import { useState, useCallback } from 'react';

// Empty initial data - no dummy data for ENVIS version
const defectsDummyData = [];

const useDefectsData = (initialDefects = defectsDummyData) => {
  const [defects, setDefects] = useState(initialDefects);

  // Add a new defect
  const addDefect = useCallback((newDefect) => {
    setDefects(prev => [newDefect, ...prev]);
  }, []);

  // Validate a defect
  const validateDefect = useCallback((defectId, isValid, note = '', newType = null, validatedBy = 'System') => {
    setDefects(prev => prev.map(defect => {
      if (defect.id === defectId) {
        const updatedDefect = {
          ...defect,
          validated: isValid,
          validationStatus: isValid ? 'Validated' : 'Rejected',
          validationNote: note,
          validatedBy,
          validatedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString().split('T')[0]
        };
        
        // Update defect type if provided
        if (newType && newType !== defect.type) {
          updatedDefect.type = newType;
        }
        
        return updatedDefect;
      }
      return defect;
    }));
  }, []);

  // Assign work order to a defect
  const assignWorkOrder = useCallback((defectId) => {
    setDefects(prev => prev.map(defect => {
      if (defect.id === defectId && defect.validated && defect.status === 'Pending') {
        const workOrderId = `WO-${Math.floor(Math.random() * 9000) + 1000}`;
        return {
          ...defect,
          hasWorkOrder: true,
          workOrderId,
          status: 'In Progress',
          lastUpdated: new Date().toISOString().split('T')[0]
        };
      }
      return defect;
    }));
  }, []);

  // Batch assign work orders
  const batchAssignWorkOrders = useCallback((defectIds) => {
    let assignedCount = 0;
    setDefects(prev => prev.map(defect => {
      if (defectIds.includes(defect.id) && defect.validated && defect.status === 'Pending' && !defect.hasWorkOrder) {
        const workOrderId = `WO-${Math.floor(Math.random() * 9000) + 1000}`;
        assignedCount++;
        return {
          ...defect,
          hasWorkOrder: true,
          workOrderId,
          status: 'In Progress',
          lastUpdated: new Date().toISOString().split('T')[0]
        };
      }
      return defect;
    }));
    return assignedCount;
  }, []);

  // Filter defects based on search term, filters, and date range
  const getFilteredDefects = useCallback((searchTerm, activeFilters, dateFilter) => {
    return defects.filter(defect => {
      // Search filter
      const matchesSearch = !searchTerm || 
        defect.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        defect.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        defect.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        defect.type.toLowerCase().includes(searchTerm.toLowerCase());

      // Severity filter
      const matchesSeverity = !activeFilters.severity || defect.severity === activeFilters.severity;

      // Status filter
      const matchesStatus = !activeFilters.status || defect.status === activeFilters.status;

      // Date filter
      const defectDate = new Date(defect.detectedOn);
      const today = new Date();
      const matchesDate = (() => {
        switch (dateFilter) {
          case 'today':
            return defectDate.toDateString() === today.toDateString();
          case 'last7days':
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            return defectDate >= sevenDaysAgo;
          case 'last30days':
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(today.getDate() - 30);
            return defectDate >= thirtyDaysAgo;
          case 'thisMonth':
            return defectDate.getMonth() === today.getMonth() && 
                   defectDate.getFullYear() === today.getFullYear();
          case 'all':
            return true;
          default:
            return true;
        }
      })();

      return matchesSearch && matchesSeverity && matchesStatus && matchesDate;
    });
  }, [defects]);

  // Get defect statistics
  const getDefectStats = useCallback(() => {
    const total = defects.length;
    const validated = defects.filter(d => d.validated).length;
    const pending = defects.filter(d => d.status === 'Pending').length;
    const inProgress = defects.filter(d => d.status === 'In Progress').length;
    const completed = defects.filter(d => d.status === 'Completed').length;
    
    const severityStats = {
      Critical: defects.filter(d => d.severity === 'Critical').length,
      High: defects.filter(d => d.severity === 'High').length,
      Medium: defects.filter(d => d.severity === 'Medium').length,
      Low: defects.filter(d => d.severity === 'Low').length
    };

    return {
      total,
      validated,
      pending,
      inProgress,
      completed,
      severityStats
    };
  }, [defects]);

  return {
    defects,
    addDefect,
    validateDefect,
    assignWorkOrder,
    batchAssignWorkOrders,
    getFilteredDefects,
    getDefectStats
  };
};

export default useDefectsData; 